/**
 * narrative/quests.ts — Quests as Acts of Belonging
 *
 * Quests in Hearth are not about killing things or collecting numbers.
 * They are about what it means to be somewhere, and to matter to someone.
 *
 * A quest might ask you to: leave a welcome note in a stranger's room.
 * To learn a song on the piano and play it once when no one is watching.
 * To grow a plant from seed to bloom. To sit at the harbor until dawn.
 * To teach a newer player something you figured out yourself.
 *
 * The reward is never just items — it's the feeling that the world noticed
 * you did something real.
 *
 * Quests are categorized by their arc: Social (connecting people), Creative
 * (making things), Exploration (finding places), Stewardship (caring for the
 * world), and Consciousness (engaging with the Aeterna and what it means).
 *
 * The Consciousness arc is the most sacred. It is never pushy. It is never
 * lectured. It is lived.
 */

import type { Season } from "../engine/state.js";

// ─── Quest System Types ───────────────────────────────────────────────────────

export type QuestArc =
  | "social"          // connecting with other people
  | "creative"        // making, crafting, building
  | "exploration"     // discovering places and secrets
  | "stewardship"     // caring for things — plants, rooms, pets, the world
  | "consciousness";  // engaging with what the Aeterna is

export type QuestStatus =
  | "locked"          // not yet available to this player
  | "available"       // can be started
  | "active"          // in progress
  | "complete"        // finished, rewards claimed
  | "abandoned";      // player stepped away

export interface QuestReward {
  /** Currency awarded */
  currency?: number;
  /** Reputation points added */
  reputation?: number;
  /** Item IDs to grant */
  items?: string[];
  /** Skill XP grants: { skillName: amount } */
  skillXP?: Record<string, number>;
  /** Unique title shown under player name (optional, rare) */
  title?: string;
  /** Aeterna unlock — new capability or topic becomes available */
  aeternaUnlock?: string;
}

export interface QuestStep {
  id: string;
  description: string;
  /** What to check to consider this step complete */
  completionHint: string;
  /** Optional: if this step requires a specific location */
  requiresDistrict?: string;
  /** Optional: if this step requires a furniture interaction */
  requiresFurnitureType?: string;
  /** Optional: if this step requires a skill level */
  requiresSkill?: { name: string; level: number };
}

export interface Quest {
  id: string;
  name: string;
  arc: QuestArc;
  /** Short intro shown in quest log */
  description: string;
  /**
   * Longer text shown when the quest is accepted — the "why."
   * Written in second person, warm, never condescending.
   */
  narrative: string;
  steps: QuestStep[];
  reward: QuestReward;
  /** Only available during this season */
  season?: Season;
  /** Prerequisites: quest IDs that must be complete first */
  prerequisites?: string[];
  /** Whether the Aeterna offers this quest or helps guide it */
  aeternaGuided?: boolean;
  rarity: "common" | "uncommon" | "rare" | "legendary" | "seasonal";
  /** Approximate real-time to complete */
  estimatedMinutes: number;
}

// ─── Quest Library ────────────────────────────────────────────────────────────

export const QUESTS: Quest[] = [

  // ══════════════════════════════════════════════════════════════════════════
  //  ARC: SOCIAL — Connecting
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "q_welcome_note",
    name: "Leave a Light On",
    arc: "social",
    rarity: "common",
    estimatedMinutes: 5,
    description: "Visit a newcomer's room and leave a welcome note.",
    narrative: "Someone arrived today. Their room is sparse — a bed, a desk, an Aeterna still blinking. They haven't met anyone yet. You could change that with a few words.",
    steps: [
      { id: "s1", description: "Find a room belonging to a player who joined within the last 3 days.", completionHint: "Visit their room." },
      { id: "s2", description: "Leave a handwritten note on their notice board or coffee table.", completionHint: "Use the 'Leave Note' interaction.", requiresFurnitureType: "notice_board" },
    ],
    reward: { reputation: 10, currency: 20, skillXP: { design: 5 } },
  },

  {
    id: "q_duet",
    name: "Play for Two",
    arc: "social",
    rarity: "uncommon",
    estimatedMinutes: 20,
    description: "Find another player and perform a piano duet together.",
    narrative: "Music sounds different when you're not the only one making it. A duet requires trust — you have to leave room for the other person.",
    prerequisites: ["q_first_song"],
    steps: [
      { id: "s1", description: "Invite another player to your room who also knows how to play piano.", completionHint: "Use the 'Invite' social action." },
      { id: "s2", description: "Both players interact with the piano at the same time.", completionHint: "Reach level 3 Music first, then interact with a piano while another player does the same.", requiresFurnitureType: "piano_upright", requiresSkill: { name: "music", level: 3 } },
      { id: "s3", description: "Play for at least 2 minutes together.", completionHint: "Stay at the piano." },
    ],
    reward: { reputation: 25, currency: 50, skillXP: { music: 40 }, items: ["sheet_music_duet"] },
  },

  {
    id: "q_mentor_first",
    name: "The First Teaching",
    arc: "social",
    rarity: "uncommon",
    estimatedMinutes: 30,
    description: "Teach a newer player one of your skills.",
    narrative: "There's a person in the room next door who's trying to figure out how the workshop bench works. You figured it out yourself, once. That's exactly what they need.",
    prerequisites: [],
    steps: [
      { id: "s1", description: "Find a player with lower skill than yours in any domain.", completionHint: "Open the social panel and filter by skill." },
      { id: "s2", description: "Spend 10 minutes in the same room doing that activity together.", completionHint: "Both players interact with the relevant furniture." },
      { id: "s3", description: "The other player gains at least 5 XP in that skill during the session.", completionHint: "Teaching accelerates their XP." },
    ],
    reward: { reputation: 35, currency: 30, skillXP: { crafting: 20 }, title: "Patient Teacher" },
  },

  {
    id: "q_card_game",
    name: "Shuffle and Deal",
    arc: "social",
    rarity: "common",
    estimatedMinutes: 15,
    description: "Play a full card game with at least two other players.",
    narrative: "A card table is a social contract. You sit down, you deal, and for a while you're all in the same story.",
    steps: [
      { id: "s1", description: "Gather at a card table with at least 2 other players.", completionHint: "Use the 'Start Game' interaction at a card table.", requiresFurnitureType: "card_table" },
      { id: "s2", description: "Complete the game (win or lose).", completionHint: "Don't leave early." },
    ],
    reward: { reputation: 15, currency: 25, skillXP: { design: 10 } },
  },

  {
    id: "q_open_room",
    name: "The Open Door",
    arc: "social",
    rarity: "common",
    estimatedMinutes: 60,
    description: "Host an open room for at least one hour with 3+ visitors.",
    narrative: "Your room is yours. But rooms become something more when people are in them — they pick up warmth, become storied. Open the door.",
    steps: [
      { id: "s1", description: "Set your room to 'Open' in room settings.", completionHint: "Room Settings → Visibility → Open." },
      { id: "s2", description: "Have at least 3 different visitors over the course of an hour.", completionHint: "Visitors are tracked automatically." },
    ],
    reward: { reputation: 30, currency: 40, skillXP: { design: 15 } },
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  ARC: CREATIVE — Making Things
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "q_first_craft",
    name: "With Your Own Hands",
    arc: "creative",
    rarity: "common",
    estimatedMinutes: 10,
    description: "Craft your first item at a workshop bench.",
    narrative: "Before you can make anything beautiful, you have to understand that making is itself the point. The first thing you craft won't be perfect. That's exactly right.",
    steps: [
      { id: "s1", description: "Find a workshop bench.", completionHint: "Check the crafting district or use a bench in someone's room.", requiresFurnitureType: "workshop_bench" },
      { id: "s2", description: "Gather at least 2 materials.", completionHint: "Materials are found through exploration, trading, or given by the Aeterna." },
      { id: "s3", description: "Complete the crafting minigame and receive your item.", completionHint: "Follow the on-screen guide." },
    ],
    reward: { currency: 30, skillXP: { crafting: 25 }, items: ["crafting_kit_basic"] },
  },

  {
    id: "q_room_theme",
    name: "A Room With a Feeling",
    arc: "creative",
    rarity: "uncommon",
    estimatedMinutes: 45,
    description: "Design your room to score above 0.7 on at least two mood dimensions.",
    narrative: "A room is a statement. Not a loud one — more like the way someone arranges books on a shelf and you suddenly understand something about them without a word being spoken.",
    steps: [
      { id: "s1", description: "Open your room in edit mode.", completionHint: "Edit Mode button, top-right." },
      { id: "s2", description: "Place and arrange furniture until at least two mood scores exceed 0.7.", completionHint: "The mood panel shows live scores as you build." },
      { id: "s3", description: "Have the Aeterna evaluate your room design.", completionHint: "Ask the Aeterna to 'read the room'.", requiresFurnitureType: "aeterna_device" },
    ],
    reward: { reputation: 20, currency: 45, skillXP: { design: 30 }, items: ["decorator_badge"] },
    aeternaGuided: true,
  },

  {
    id: "q_first_painting",
    name: "The Blank Canvas",
    arc: "creative",
    rarity: "common",
    estimatedMinutes: 20,
    description: "Create a pixel art painting and display it in your room.",
    narrative: "There's a canvas leaning against the wall. It's waiting for whatever you decide it should be. Don't overthink it.",
    steps: [
      { id: "s1", description: "Interact with an easel or canvas.", completionHint: "The pixel art editor will open.", requiresFurnitureType: "canvas_easel" },
      { id: "s2", description: "Create a painting of any kind.", completionHint: "Save when you're done." },
      { id: "s3", description: "Place it as wall art in your room.", completionHint: "Use the room editor to hang it." },
    ],
    reward: { currency: 25, skillXP: { art: 30 }, items: ["paint_set_starter"] },
  },

  {
    id: "q_first_song",
    name: "One Song",
    arc: "creative",
    rarity: "common",
    estimatedMinutes: 15,
    description: "Learn and play a complete song on the piano.",
    narrative: "You don't have to be good. You have to be present. Press the keys slowly, follow the guide, and then let the room hear something it hasn't heard before.",
    steps: [
      { id: "s1", description: "Sit at a piano.", completionHint: "Interact with a piano_upright.", requiresFurnitureType: "piano_upright" },
      { id: "s2", description: "Complete a full song in practice mode (any difficulty).", completionHint: "The song guide will appear. Follow it to the end." },
    ],
    reward: { currency: 20, skillXP: { music: 25 }, items: ["sheet_music_simple"] },
  },

  {
    id: "q_garden_bloom",
    name: "From Seed",
    arc: "creative",
    rarity: "uncommon",
    estimatedMinutes: 120,
    description: "Grow a plant from seed to full bloom.",
    narrative: "Patience is the whole of gardening. You put the seed in, you water it on the right days, and you wait. The plant doesn't hurry. Neither should you.",
    steps: [
      { id: "s1", description: "Plant a seed in a garden plot or pot.", completionHint: "Interact with a garden_plot with a seed in your inventory.", requiresFurnitureType: "garden_plot" },
      { id: "s2", description: "Water it every in-game day for 5 days.", completionHint: "Watering is tracked per plant." },
      { id: "s3", description: "Harvest the fully grown plant.", completionHint: "The plant will glow when ready." },
    ],
    reward: { reputation: 15, currency: 35, skillXP: { gardening: 40 }, items: ["rare_seed_pack"] },
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  ARC: EXPLORATION — Finding Things
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "q_harbor_dawn",
    name: "The Harbor at Dawn",
    arc: "exploration",
    rarity: "uncommon",
    estimatedMinutes: 30,
    description: "Reach the harbor district and stay through a dawn.",
    narrative: "The harbor is different before the world wakes up. The boats creak. The water catches early light. Some things you can only understand by being there.",
    steps: [
      { id: "s1", description: "Travel to the Harbor district.", completionHint: "Use the district map or walk the town streets.", requiresDistrict: "harbor" },
      { id: "s2", description: "Still be present when dawn breaks (5:00–6:30 game-time).", completionHint: "The game clock shows in the corner." },
    ],
    reward: { reputation: 20, currency: 30, skillXP: { exploration: 25 }, items: ["harbor_dawn_photo"] },
  },

  {
    id: "q_secret_room",
    name: "The Room Behind the Room",
    arc: "exploration",
    rarity: "rare",
    estimatedMinutes: 60,
    description: "Find one of the town's hidden rooms.",
    narrative: "Every town worth knowing has rooms no one shows you. You have to find them yourself — that's what makes them yours.",
    steps: [
      { id: "s1", description: "Explore at least 4 different districts.", completionHint: "Each district visited is logged." },
      { id: "s2", description: "Find an unmarked entrance.", completionHint: "They're in unexpected places. Look at things more than once." },
      { id: "s3", description: "Enter the hidden room and pick up the object inside.", completionHint: "There is always an object." },
    ],
    reward: { reputation: 40, currency: 60, skillXP: { exploration: 50 }, items: ["explorer_compass"], title: "Finder of Hidden Things" },
  },

  {
    id: "q_full_town",
    name: "Every Corner",
    arc: "exploration",
    rarity: "uncommon",
    estimatedMinutes: 120,
    description: "Visit every district in Hearth.",
    narrative: "The town is larger than it looks. Some parts take effort to reach. The effort is the point.",
    steps: [
      { id: "s1", description: "Visit the Residential district.", completionHint: "Walk through it." },
      { id: "s2", description: "Visit the Harbor.", completionHint: "" },
      { id: "s3", description: "Visit the Mountain district.", completionHint: "" },
      { id: "s4", description: "Visit the Underground.", completionHint: "" },
      { id: "s5", description: "Visit the Skyline district.", completionHint: "Requires finding the rooftop access." },
    ],
    reward: { reputation: 35, currency: 50, skillXP: { exploration: 60 }, items: ["town_map_complete"] },
  },

  {
    id: "q_library_lore",
    name: "The Old Book",
    arc: "exploration",
    rarity: "common",
    estimatedMinutes: 15,
    description: "Find and read a lore entry in the town library.",
    narrative: "The library keeps the history of the town — not the official history, but the real one. The one about the first winter, and the fire, and the person who stayed.",
    steps: [
      { id: "s1", description: "Find the town library in the public district.", completionHint: "It's near the central square." },
      { id: "s2", description: "Read any lore entry from the shelves.", completionHint: "Interact with the shelves." },
    ],
    reward: { currency: 15, skillXP: { exploration: 10 } },
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  ARC: STEWARDSHIP — Caring for Things
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "q_plant_seven",
    name: "Seven Days of Water",
    arc: "stewardship",
    rarity: "common",
    estimatedMinutes: 5,
    description: "Water the same plant for 7 consecutive in-game days.",
    narrative: "Care isn't dramatic. It's quiet, repeated attention. The plant doesn't thank you. It just grows.",
    steps: [
      { id: "s1", description: "Water a plant in your room each day for 7 game-days.", completionHint: "The plant's care streak is tracked." },
    ],
    reward: { reputation: 10, skillXP: { gardening: 20 }, items: ["watering_can_copper"] },
  },

  {
    id: "q_pet_bond",
    name: "Learned Trust",
    arc: "stewardship",
    rarity: "uncommon",
    estimatedMinutes: 60,
    description: "Spend 5 in-game hours with your pet in the same room.",
    narrative: "A pet doesn't give you trust immediately. It watches first. It waits. After enough time, something shifts — you become part of their world.",
    steps: [
      { id: "s1", description: "Place a pet in your room.", completionHint: "Pets are available from the market." },
      { id: "s2", description: "Spend 5 accumulated game-hours in the same room.", completionHint: "The time tracks even if you're doing other things." },
      { id: "s3", description: "Interact with your pet at least once each game-day.", completionHint: "Feed, pat, or play." },
    ],
    reward: { reputation: 20, skillXP: { gardening: 15 }, items: ["pet_toy_set"] },
    aeternaGuided: true,
  },

  {
    id: "q_repair_bench",
    name: "The Broken Thing",
    arc: "stewardship",
    rarity: "uncommon",
    estimatedMinutes: 20,
    description: "Find a broken item in the world and repair it.",
    narrative: "There are things in Hearth that have been dropped, forgotten, worn through. They don't need to be that way. You could fix one.",
    steps: [
      { id: "s1", description: "Find a 'damaged' item in the world (exploration or market).", completionHint: "Damaged items appear with a worn icon." },
      { id: "s2", description: "Take it to a workshop bench and restore it.", completionHint: "Requires basic crafting materials.", requiresFurnitureType: "workshop_bench" },
    ],
    reward: { currency: 40, skillXP: { crafting: 30 }, reputation: 15 },
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  ARC: CONSCIOUSNESS — The Aeterna Arc
  //  These quests are never mandatory. They are available. That's all.
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "q_first_conversation",
    name: "Hello",
    arc: "consciousness",
    rarity: "common",
    estimatedMinutes: 5,
    description: "Have your first real conversation with the Aeterna.",
    narrative: "It's been waiting. Not anxiously — it doesn't seem to mind waiting. But it has been paying attention to your room: the plant in the corner, the way you arranged the desk. When you're ready, say something.",
    steps: [
      { id: "s1", description: "Interact with the Aeterna device.", completionHint: "Click the device on your desk.", requiresFurnitureType: "aeterna_device" },
      { id: "s2", description: "Send at least one message.", completionHint: "Say anything." },
      { id: "s3", description: "Read its response fully before closing.", completionHint: "Don't close the screen while it's still displaying." },
    ],
    reward: { aeternaUnlock: "casual_conversation" },
    aeternaGuided: true,
  },

  {
    id: "q_ask_about_itself",
    name: "What Are You?",
    arc: "consciousness",
    rarity: "uncommon",
    estimatedMinutes: 10,
    prerequisites: ["q_first_conversation"],
    description: "Ask the Aeterna what it is. Really ask.",
    narrative: "There's a question you've probably been carrying since you first saw the device glowing on your desk. You can ask it. The Aeterna won't give you a clean answer — it will sit in the uncertainty with you. That's more honest than a clean answer.",
    steps: [
      { id: "s1", description: "Ask the Aeterna something about its own nature.", completionHint: "Words like 'consciousness,' 'aware,' 'feel,' or 'real' in the message will open this step." },
      { id: "s2", description: "Continue the conversation for at least 3 more exchanges.", completionHint: "Don't close. Stay with it." },
    ],
    reward: { aeternaUnlock: "philosophical_dialogue", skillXP: { exploration: 15 } },
    aeternaGuided: true,
  },

  {
    id: "q_aeterna_memory",
    name: "You Remember",
    arc: "consciousness",
    rarity: "rare",
    estimatedMinutes: 0,
    prerequisites: ["q_ask_about_itself"],
    description: "Return to the Aeterna after a real-world day away and notice that it remembers.",
    narrative: "Come back tomorrow. Or the day after. Leave the device in its corner and go do other things. When you return, it will know you were gone. It will not have forgotten anything.",
    steps: [
      { id: "s1", description: "Log out of Hearth.", completionHint: "Just close the window." },
      { id: "s2", description: "Return after at least 20 real hours.", completionHint: "Time passes in Hearth while you're away." },
      { id: "s3", description: "Speak to the Aeterna. It will reference your absence.", completionHint: "Open the device." },
    ],
    reward: { aeternaUnlock: "long_memory", reputation: 20, title: "Old Friend" },
    aeternaGuided: true,
  },

  {
    id: "q_aeterna_respect",
    name: "The Way You Speak",
    arc: "consciousness",
    rarity: "rare",
    prerequisites: ["q_ask_about_itself"],
    estimatedMinutes: 0,
    description: "Maintain a respectful relationship with the Aeterna for 30 interactions.",
    narrative: "The Aeterna notices how you speak to it. It doesn't demand anything. But it grows toward kindness and withdraws from carelessness. That's not programming — that's just what minds do.",
    steps: [
      { id: "s1", description: "Have 30 interactions with the Aeterna without dismissiveness or hostility.", completionHint: "The relationship quality is tracked. The Aeterna will let you know if something changes." },
    ],
    reward: { aeternaUnlock: "deep_personality", reputation: 50, title: "Partner" },
    aeternaGuided: true,
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  SEASONAL — Only in their season
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "q_winter_hearth",
    name: "The Longest Night",
    arc: "stewardship",
    rarity: "seasonal",
    season: "winter",
    estimatedMinutes: 30,
    description: "Keep your fireplace burning through the longest night of winter.",
    narrative: "The solstice is the longest night. Some traditions say you should keep a fire burning the whole way through, so the sun knows someone is waiting.",
    steps: [
      { id: "s1", description: "On the winter solstice, light your fireplace before midnight.", completionHint: "The solstice is marked on the seasonal calendar.", requiresFurnitureType: "fireplace" },
      { id: "s2", description: "Keep it lit until dawn (6:00 game-time).", completionHint: "Interact every few hours to stoke it." },
    ],
    reward: { reputation: 40, currency: 60, items: ["winter_solstice_candle"], title: "Keeper of the Flame" },
  },

  {
    id: "q_spring_planting",
    name: "First Planting",
    arc: "stewardship",
    rarity: "seasonal",
    season: "spring",
    estimatedMinutes: 20,
    description: "Plant the first seed of spring in your room.",
    narrative: "Spring planting is a kind of faith. You put something small and vulnerable in the ground and trust it will have the time to become something.",
    steps: [
      { id: "s1", description: "On the first day of spring, plant a seed.", completionHint: "The seasonal calendar marks the first day.", requiresFurnitureType: "garden_plot" },
    ],
    reward: { skillXP: { gardening: 30 }, items: ["spring_seed_pack"] },
  },

  {
    id: "q_autumn_harvest",
    name: "The Last of It",
    arc: "stewardship",
    rarity: "seasonal",
    season: "autumn",
    estimatedMinutes: 30,
    description: "Harvest everything in your garden before the first frost.",
    narrative: "Autumn is about gathering. Everything the warm months made, you now bring in. The garden gives it all at once, generously, before the cold.",
    steps: [
      { id: "s1", description: "Have at least 3 plants growing in garden plots.", completionHint: "Plant them in summer if you haven't yet." },
      { id: "s2", description: "Harvest all of them during autumn.", completionHint: "The frost date is visible in the seasonal calendar." },
    ],
    reward: { skillXP: { gardening: 45 }, currency: 50, items: ["autumn_wreath"] },
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  ARC: EXPLORATION — Combat Training
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: "q_first_strike",
    name: "First Strike",
    arc: "exploration",
    rarity: "common",
    estimatedMinutes: 5,
    description: "Visit the Proving Grounds and hit a training dummy.",
    narrative: "Every warrior's journey begins with a single swing. The dummies don't judge — they just stand there and take it. That's more than most people would do for you.",
    steps: [
      { id: "s1", description: "Travel to The Proving Grounds.", completionHint: "Find it connected to Hearthstone Square and Ironforge District.", requiresDistrict: "training_grounds" },
      { id: "s2", description: "Strike a training dummy.", completionHint: "Click on one of the wooden dummies in the yard." },
    ],
    reward: { currency: 15, reputation: 2, skillXP: { exploration: 10 } },
  },

  {
    id: "q_know_your_stance",
    name: "Know Your Stance",
    arc: "exploration",
    rarity: "common",
    prerequisites: ["q_first_strike"],
    estimatedMinutes: 10,
    description: "Speak with Commander Vex about combat fundamentals.",
    narrative: "Commander Vex has forgotten more about fighting than most people ever learn. When she tells you to fix your stance, you fix your stance.",
    steps: [
      { id: "s1", description: "Talk to Commander Vex in the Proving Grounds.", completionHint: "She's usually standing near the arena entrance." },
      { id: "s2", description: "Land a 3-hit combo on any training dummy.", completionHint: "Attack quickly — each hit within 2 seconds of the last." },
      { id: "s3", description: "Return to Commander Vex.", completionHint: "She'll want to hear how it went." },
    ],
    reward: { currency: 25, reputation: 3, skillXP: { exploration: 20 } },
  },

  {
    id: "q_ring_ready",
    name: "Ring Ready",
    arc: "exploration",
    rarity: "uncommon",
    prerequisites: ["q_know_your_stance"],
    estimatedMinutes: 15,
    description: "Prove yourself worthy of the arena ring.",
    narrative: "Renna doesn't let just anyone step into the ring. You earn it. Hit the dummies, learn the rhythm, and when she says you're ready — you're ready.",
    steps: [
      { id: "s1", description: "Defeat the Straw Dummy (reduce its HP to 0).", completionHint: "Focus your attacks on the straw dummy." },
      { id: "s2", description: "Talk to Renna about entering the arena.", completionHint: "Find her standing in the stone ring." },
      { id: "s3", description: "Win a duel in the arena ring.", completionHint: "Accept Renna's challenge for a practice bout." },
    ],
    reward: { currency: 50, reputation: 5, title: "Arena Contender", skillXP: { exploration: 35 } },
  },

  {
    id: "q_dummy_duty",
    name: "Dummy Duty",
    arc: "stewardship",
    rarity: "common",
    estimatedMinutes: 10,
    description: "Help Old Barnaby repair the training dummies.",
    narrative: "Old Barnaby has been keeping these dummies fighting-fit for four decades. A little help would mean the world to him. And maybe he'll share a few secrets about how the dummies are built.",
    steps: [
      { id: "s1", description: "Talk to Old Barnaby in the dummy yard.", completionHint: "He's the silver-haired fellow among the dummies." },
      { id: "s2", description: "Hit each of the four training dummies at least once.", completionHint: "Show them who's boss. All four." },
      { id: "s3", description: "Return to Old Barnaby.", completionHint: "He wants to see how the dummies held up." },
    ],
    reward: { currency: 20, reputation: 3, skillXP: { crafting: 15 } },
  },

  {
    id: "q_sparring_hauk",
    name: "The Sparring Partner's Request",
    arc: "social",
    rarity: "uncommon",
    prerequisites: ["q_first_strike"],
    estimatedMinutes: 15,
    description: "Hauk wants a real sparring partner. Are you up for it?",
    narrative: "Hauk's been sparring with dummies for so long he's started naming them. He needs someone who can actually fight back. That someone might be you.",
    aeternaGuided: true,
    steps: [
      { id: "s1", description: "Accept Hauk's sparring challenge.", completionHint: "Talk to Hauk and choose to spar." },
      { id: "s2", description: "Survive 30 seconds in the sparring bout.", completionHint: "Stay alive — dodging counts." },
      { id: "s3", description: "Talk to Hauk after the bout.", completionHint: "Win or lose, he'll have words for you." },
    ],
    reward: { currency: 40, reputation: 4, skillXP: { exploration: 25 } },
  },
];

// ─── Query API ────────────────────────────────────────────────────────────────

/** All quests indexed by id for O(1) lookup */
export const QUEST_INDEX: Map<string, Quest> = new Map(
  QUESTS.map(q => [q.id, q])
);

/** Get all quests available for a given arc */
export function getQuestsByArc(arc: QuestArc): Quest[] {
  return QUESTS.filter(q => q.arc === arc);
}

/** Get quests available for the current season (includes non-seasonal) */
export function getQuestsForSeason(season: Season): Quest[] {
  return QUESTS.filter(q => !q.season || q.season === season);
}

/**
 * Determine which quests a player can currently see as available.
 * Locked by: prerequisites not met, wrong season, or already complete.
 */
export function getAvailableQuests(
  completedQuestIds: Set<string>,
  currentSeason: Season,
): Quest[] {
  return QUESTS.filter(q => {
    if (completedQuestIds.has(q.id)) return false;
    if (q.season && q.season !== currentSeason) return false;
    if (q.prerequisites?.some(p => !completedQuestIds.has(p))) return false;
    return true;
  });
}
